import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

const TYPE_TONE: Record<string, string> = {
  zone:  'bg-purple-50 text-purple-700 ring-purple-200',
  aisle: 'bg-blue-50 text-blue-700 ring-blue-200',
  rack:  'bg-amber-50 text-amber-800 ring-amber-200',
  shelf: 'bg-sage-50 text-sage-700 ring-sage-200',
  bin:   'bg-canvas-subtle text-ink-700 ring-ink-200',
};

export function LocationsPage() {
  const qc = useQueryClient();
  const [selectedWh, setSelectedWh] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showWhCreate, setShowWhCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const locQuery = useQuery({
    queryKey: ['inv', 'locations', selectedWh],
    queryFn: () => inventoryApi.listLocations(selectedWh!),
    enabled: !!selectedWh,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => inventoryApi.deleteLocation(id),
    onSuccess: () => { toast.success('Location deleted'); qc.invalidateQueries({ queryKey: ['inv', 'locations', selectedWh] }); setDeletingId(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => inventoryApi.updateLocation(id, payload),
    onSuccess: () => { toast.success('Location updated'); qc.invalidateQueries({ queryKey: ['inv', 'locations', selectedWh] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const tree = useMemo(() => {
    if (!locQuery.data) return [];
    const byId = new Map<string, any>();
    const roots: any[] = [];
    for (const loc of locQuery.data) byId.set(loc.id, { ...loc, children: [] });
    for (const loc of locQuery.data) {
      const node = byId.get(loc.id)!;
      if (loc.parent_id && byId.has(loc.parent_id)) byId.get(loc.parent_id).children.push(node);
      else roots.push(node);
    }
    return roots;
  }, [locQuery.data]);

  const typeCounts = useMemo(() => {
    const c: Record<string, number> = { zone:0, aisle:0, rack:0, shelf:0, bin:0 };
    for (const loc of locQuery.data ?? []) { if (c[loc.type] !== undefined) c[loc.type]++; }
    return c;
  }, [locQuery.data]);

  const deletingNode = deletingId ? locQuery.data?.find((l: any) => l.id === deletingId) : null;

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="Inventory" title="Warehouse locations"
        subtitle="Zone → aisle → rack → shelf → bin hierarchy per warehouse."
        actions={selectedWh ? <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New location</button> : null}
      />

      <div className="card p-4 mb-6">
        <label className="label">Warehouse</label>
        <div className="flex items-center gap-2">
          <select className="input max-w-md" value={selectedWh ?? ''} onChange={(e) => setSelectedWh(e.target.value || null)}>
            <option value="">Select a warehouse…</option>
            {whQuery.data?.map((w: any) => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
          </select>
          <button className="btn-secondary whitespace-nowrap" onClick={() => setShowWhCreate(true)}>+ New warehouse</button>
        </div>
      </div>

      {showWhCreate && <WarehouseModal
        onClose={() => setShowWhCreate(false)}
        onSaved={(id?: string) => { qc.invalidateQueries({ queryKey: ['warehouses'] }); setShowWhCreate(false); if (id) setSelectedWh(id); }} />}

      {!selectedWh ? (
        <div className="card"><EmptyState message="Select a warehouse" hint="Pick a warehouse above to view or manage its location hierarchy." /></div>
      ) : locQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : locQuery.error ? (
        <ErrorState error={locQuery.error} />
      ) : (
        <>
          {(locQuery.data?.length ?? 0) > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
              {['zone','aisle','rack','shelf','bin'].map((t) => (
                <div key={t} className="card p-4 flex items-center gap-3">
                  <div className={clsx('font-mono font-bold text-2xl tabular-nums', t === 'bin' ? 'text-sage-700' : 'text-ink-900')}>{typeCounts[t]}</div>
                  <div className="text-2xs text-ink-500 uppercase tracking-wider font-semibold capitalize">{t}s</div>
                </div>
              ))}
            </div>
          )}

          {tree.length === 0 ? (
            <div className="card">
              <EmptyState message="No locations defined"
                hint="Create the first location. Start with a zone, then add aisles, racks, shelves, and bins inside."
                action={<button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> Create first location</button>}
              />
            </div>
          ) : (
            <div className="card p-5">
              <ul className="text-sm space-y-0.5">
                {tree.map((node) => <LocationNode key={node.id} node={node} depth={0} onEdit={setEditing} onDelete={setDeletingId} />)}
              </ul>
            </div>
          )}
        </>
      )}

      {showCreate && selectedWh && (
        <LocationModal warehouseId={selectedWh} existingLocations={locQuery.data ?? []}
          onClose={() => setShowCreate(false)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['inv', 'locations', selectedWh] }); setShowCreate(false); }} />
      )}
      {editing && selectedWh && (
        <LocationModal initial={editing} warehouseId={selectedWh} existingLocations={locQuery.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(payload) => updateMut.mutate({ id: editing.id, payload })} />
      )}
      {deletingId && (
        <Modal
          onClose={() => setDeletingId(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Inventory · Locations</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete location?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              Delete <span className="font-mono font-semibold text-ink-900">{deletingNode?.code}</span> — {deletingNode?.name}?
              Child locations will also be removed.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeletingId(null)}>Cancel</button>
            <button className="btn-primary !bg-danger hover:!bg-red-700" disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate(deletingId)}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete location'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LocationNode({ node, depth, onEdit, onDelete }: { node: any; depth: number; onEdit: (n: any) => void; onDelete: (id: string) => void }) {
  const typeTone = TYPE_TONE[node.type] || 'bg-canvas-subtle text-ink-600 ring-ink-200';
  return (
    <li>
      <div className="flex items-center gap-2.5 py-1.5 px-2 rounded-md hover:bg-canvas-subtle/50 transition-colors group"
        style={{ paddingLeft: depth * 20 + 8 }}>
        {depth > 0 && <svg width="10" height="10" viewBox="0 0 10 10" className="text-ink-300 flex-shrink-0"><path d="M 0 0 L 0 5 L 10 5" stroke="currentColor" strokeWidth="1" fill="none" /></svg>}
        <span className="font-mono text-2xs font-semibold text-ink-900">{node.code}</span>
        <span className="text-sm text-ink-700 flex-1 truncate">{node.name}</span>
        <span className={clsx('badge ring-1 ring-inset text-2xs capitalize', typeTone)}>{node.type}</span>
        <div className="hidden group-hover:flex items-center gap-1 ml-2">
          <button className="text-xs font-medium px-2 py-0.5 rounded hover:bg-canvas-subtle border border-ink-200 text-ink-600 transition-colors"
            onClick={() => onEdit(node)}>Edit</button>
          <button className="text-xs font-medium px-2 py-0.5 rounded hover:bg-red-50 border border-red-200 text-danger transition-colors"
            onClick={() => onDelete(node.id)}>Delete</button>
        </div>
      </div>
      {node.children.length > 0 && (
        <ul className="space-y-0.5">
          {node.children.map((c: any) => <LocationNode key={c.id} node={c} depth={depth + 1} onEdit={onEdit} onDelete={onDelete} />)}
        </ul>
      )}
    </li>
  );
}

function LocationModal({ initial, warehouseId, existingLocations, onClose, onSaved }: {
  initial?: any; warehouseId: string; existingLocations: any[]; onClose: () => void; onSaved: (p?: any) => void;
}) {
  const isEdit = !!initial;
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<'zone'|'aisle'|'rack'|'shelf'|'bin'>(initial?.type ?? 'bin');
  const [parentId, setParentId] = useState(initial?.parent_id ?? '');

  const qc = useQueryClient();
  const createMut = useMutation({
    mutationFn: () => inventoryApi.createLocation({ warehouseId, code: code.trim(), name: name.trim(), type, parentId: parentId || null }),
    onSuccess: () => { toast.success('Location created'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const otherLocations = existingLocations.filter((l: any) => l.id !== initial?.id);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit location' : 'New location'}</h2>
        {!isEdit && <p className="text-sm text-ink-500 mt-1">Add a zone, aisle, rack, shelf, or bin to the hierarchy.</p>}
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Code <span className="text-danger">*</span></label>
            <input className="input font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="A1-01" disabled={isEdit} />
          </div>
          <div>
            <label className="label">Type</label>
            <select className="input" value={type} onChange={(e) => setType(e.target.value as any)} disabled={isEdit}>
              <option value="zone">Zone</option>
              <option value="aisle">Aisle</option>
              <option value="rack">Rack</option>
              <option value="shelf">Shelf</option>
              <option value="bin">Bin</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Back storage, row A" />
        </div>
        {!isEdit && (
          <div>
            <label className="label">Parent location <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
            <select className="input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">(Top-level)</option>
              {otherLocations.map((l: any) => <option key={l.id} value={l.id}>{l.code} — {l.name} ({l.type})</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!code.trim() || !name.trim() || createMut.isPending}
          onClick={() => isEdit ? onSaved({ name: name.trim() }) : createMut.mutate()}>
          {createMut.isPending ? 'Creating…' : (isEdit ? 'Save changes' : 'Create location')}
        </button>
      </div>
    </Modal>
  );
}

const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function WarehouseModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id?: string) => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('main');

  const mut = useMutation({
    mutationFn: () => inventoryApi.createWarehouse({
      code: code.trim() ? code.trim().toUpperCase() : undefined,
      name: name.trim(),
      type,
    }),
    onSuccess: (r: any) => { toast.success(`Warehouse created · ${r.code}`); onSaved(r.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
      <h3 className="text-lg font-bold text-ink-900 mb-4">New warehouse</h3>
      <div className="space-y-3">
        <div>
          <label className="label">Code <span className="text-ink-400 font-normal">(auto if blank)</span></label>
          <input className="input font-mono uppercase" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Auto (WH-001)" />
        </div>
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Main Warehouse" />
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="main">Main</option>
            <option value="transit">Transit</option>
            <option value="damaged">Damaged</option>
            <option value="quarantine">Quarantine</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name.trim() || mut.isPending}
          onClick={() => mut.mutate()}>{mut.isPending ? 'Creating…' : 'Create warehouse'}</button>
      </div>
    </Modal>
  );
}

function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
